import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { createPaymentIntent, confirmPayment } from '../../../api/onboarding';

// Load Stripe outside of component to avoid recreating on every render.
// Guard against missing publishable key so we don't call loadStripe(undefined)
const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = publishableKey ? loadStripe(publishableKey) : null;

// Payment Element configuration for international support
const PaymentElementOptions = {
  layout: "tabs",
  paymentMethodOrder: ['card'],
  fields: {
    billingDetails: {
      address: {
        country: 'auto'  // Auto-detect or allow selection
      }
    }
  },
  defaultValues: {
    billingDetails: {
      address: {
        country: 'GB'  // Default to UK
      }
    }
  }
};

const PaymentForm = ({ leadId, onPaymentSuccess, onPaymentError, setIsLoading, setupIntentId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  // Debug logging
  useEffect(() => {
    console.log('PaymentForm mounted with:', {
      leadId,
      setupIntentId,
      hasStripe: !!stripe,
      hasElements: !!elements,
      stripePublishableKey: publishableKey?.substring(0, 7) + '...'
    });
  }, [leadId, setupIntentId, stripe, elements]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      console.error('Stripe not loaded');
      return;
    }

    setProcessing(true);
    setIsLoading(true);

    try {
      // Confirm the setup intent using Payment Element
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href, // Required but not used for embedded
        },
        redirect: 'if_required'  // Stay on page for embedded experience
      });

      if (error) {
        console.error('Stripe confirmation error:', error);
        onPaymentError(error.message);
        setProcessing(false);
        setIsLoading(false);
        return;
      }

      console.log('Setup intent confirmed:', setupIntent);

      // Now call our backend to complete the payment process
      const confirmResponse = await confirmPayment(leadId, {
        setup_intent_id: setupIntent.id
      });

      if (confirmResponse.success) {
        console.log('Payment confirmed successfully');
        onPaymentSuccess(confirmResponse);
      } else {
        console.error('Payment confirmation failed:', confirmResponse);
        onPaymentError('Payment confirmation failed');
      }
    } catch (error) {
      console.error('Error during payment confirmation:', error);
      onPaymentError('An error occurred during payment processing');
    } finally {
      setProcessing(false);
      setIsLoading(false);
    }
  };

  if (!stripe || !elements) {
    return (
      <div className="payment-loading">
        <p>Loading payment form...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <div className="payment-element-container">
        <PaymentElement 
          options={PaymentElementOptions}
        />
      </div>
      
      <button
        type="submit"
        disabled={processing || !stripe}
        className="payment-submit-button"
      >
        {processing ? 'Processing...' : 'Complete Payment Setup'}
      </button>
    </form>
  );
};

const EmbeddedPayment = ({ leadId, onPaymentSuccess, onPaymentError, setIsLoading, paymentPreload }) => {
  const [clientSecret, setClientSecret] = useState(paymentPreload?.clientSecret || null);
  const [setupIntentId, setSetupIntentId] = useState(paymentPreload?.setupIntentId || null);
  const [isInitialized, setIsInitialized] = useState(paymentPreload?.isReady || false);
  const [retryCount, setRetryCount] = useState(0); // Track retry attempts
  
  console.log('EmbeddedPayment render - Stripe Configuration:', {
    leadId,
    hasStripe: !!stripePromise,
    hasPublishableKey: !!publishableKey,
    keyPrefix: publishableKey?.substring(0, 7) + '...',
    isDevelopmentMode: process.env.NODE_ENV === 'development',
    retryCount
  });

  // Enhanced payment error handler that resets setup intent for retry
  const handlePaymentError = (errorMessage) => {
    console.error('Payment error occurred:', errorMessage);
    
    // Reset setup intent state to allow fresh attempt
    setClientSecret(null);
    setSetupIntentId(null);
    setIsInitialized(false);
    setRetryCount(prev => prev + 1);
    
    // Call parent error handler
    onPaymentError(errorMessage);
  };

  // Get client secret for Elements configuration
  useEffect(() => {
    if (!leadId) return;
    
    // Skip API call if we have preloaded data and it's ready
    if (paymentPreload?.isReady && paymentPreload.clientSecret && paymentPreload.setupIntentId) {
      console.log('Using preloaded payment intent:', {
        clientSecret: paymentPreload.clientSecret,
        setupIntentId: paymentPreload.setupIntentId
      });
      setClientSecret(paymentPreload.clientSecret);
      setSetupIntentId(paymentPreload.setupIntentId);
      setIsInitialized(true);
      return;
    }

    const fetchClientSecret = async () => {
      try {
        setIsLoading(true);
        console.log(`Fetching client secret for lead ${leadId} (attempt ${retryCount + 1})`);
        
        const response = await createPaymentIntent(leadId);
        if (response.success) {
          setClientSecret(response.client_secret);
          setSetupIntentId(response.setup_intent_id);
          setIsInitialized(true);
          console.log('Payment intent initialized:', {
            clientSecret: response.client_secret,
            setupIntentId: response.setup_intent_id,
            attempt: retryCount + 1
          });
        } else {
          console.error('Failed to initialize payment:', response);
          handlePaymentError('Failed to initialize payment form');
        }
      } catch (error) {
        console.error('Error fetching client secret:', error);
        handlePaymentError('Failed to initialize payment form');
      } finally {
        setIsLoading(false);
      }
    };

    fetchClientSecret();
  }, [leadId, retryCount, setIsLoading, paymentPreload]); // Added paymentPreload to dependency array

  if (!stripePromise) {
    return (
      <div className="payment-error">
        <p>Stripe configuration error. Please contact support.</p>
      </div>
    );
  }

    // Don't render Elements until we have a clientSecret
  if (!clientSecret) {
    return (
      <div className="payment-loading">
        <p>Initializing payment form...</p>
      </div>
    );
  }

  // Configure Elements for international payments
  const elementsOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#0570de',
        colorBackground: '#ffffff',
        colorText: '#30313d',
        colorDanger: '#df1b41',
        fontFamily: 'Ideal Sans, system-ui, sans-serif',
        spacingUnit: '2px',
        borderRadius: '4px',
      }
    },
    loader: 'auto'
  };

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <PaymentForm
        leadId={leadId}
        setupIntentId={setupIntentId}
        onPaymentSuccess={onPaymentSuccess}
        onPaymentError={onPaymentError}
        setIsLoading={setIsLoading}
      />
    </Elements>
  );
};

export default EmbeddedPayment; 
